import type { RouteInfoWithAttributes } from '@ember/routing/route-info';
import type RouterService from '@ember/routing/router-service';
import type PublicTransition from '@ember/routing/transition';
import { next } from '@ember/runloop';
import { capitalize as uppercaseFirstLetter } from '@ember/string';
import { htmlSafe } from '@ember/template';

import { isPlaywrightEmail } from '@databox/ember-integrations/lib/helpers/helpers';
import $ from 'jquery';
import moment from 'moment';

import 'moment-timezone';

import type { DataLabelPart, DSData, DSSetup, PaginationDirection } from 'webapp-types';
// need to be imported once to add the extension
import {
	AppEnv,
	DataLabelType,
	DeliveryType,
	FilterItemType,
	ItemListView,
	Unit,
} from 'webapp/enums/enums';

// from app/components/common/list/lazy-load.gts
export const PopularLazyLoadSelector = {
	[ItemListView.GRID]: {
		root: 'ul',
		item: 'li',
	},
	[ItemListView.TABLE]: {
		root: 'table',
		item: 'tr',
	},
} as const;

/**
 * Tailwind `md` breakpoint in px. Widths >= this render the desktop layout (left nav sidebar);
 * widths < this render the mobile layout (top nav bar) — matching the React navigation.
 * MUST stay in sync with @screen_md in app/styles/functions.less
 * (its max-width complement there is @screen_md_below = this - 0.02).
 */
export const MD_BREAKPOINT = 768;

window.moment = moment; // expose it for global use

// cache initial query params before Ember router strips them
const initialSearchParams = new URLSearchParams(window.location.search);

export function normalizeToNumberArray(raw: string | number | number[] | undefined): number[] {
	return Array.isArray(raw)
		? raw
		: String(raw ?? '')
				.split(',')
				.map((id) => Number(id.trim()))
				.filter((id) => Number.isFinite(id) && id > 0);
}

// used for Genie embedded boards — popups are blocked inside iframes
export function arePopupsAllowed(): boolean {
	return !isDOMLoadedInIframe() || initialSearchParams.get('popups') !== 'false';
}

export function isDOMLoadedInIframe(): boolean {
	return window.self !== window.top;
}

function equal(a: unknown, b: unknown, alphabeticalKeys = false): boolean {
	let areEqual = a === b;
	if (!areEqual && a && b && typeof a === 'object' && typeof b === 'object') {
		if ((a as unknown[]).length !== (b as unknown[]).length) {
			areEqual = false;
		} else {
			if (alphabeticalKeys) {
				areEqual = stringifyAlphabetical(a) === stringifyAlphabetical(b);
			} else {
				areEqual = JSON.stringify(a) === JSON.stringify(b);
			}
		}
	}
	return areEqual;
}

// accepts any object: one without `isDestroyed` / `isDestroying` (e.g. a plain `Serviced` class) counts as alive
function isAlive(obj: object | null | undefined): boolean {
	const destroyable = obj as { isDestroyed?: boolean; isDestroying?: boolean } | null | undefined;
	return !!destroyable && !destroyable.isDestroyed && !destroyable.isDestroying;
}

function utf8_to_b64(str: string): string {
	return btoa(
		encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_match: string, p1: string) => {
			return String.fromCharCode(parseInt(`0x${p1}`));
		}),
	);
}

function b64_to_utf8(str: string): string {
	return decodeURIComponent(
		atob(str)
			.split('')
			.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
			.join(''),
	);
}

function blobUrlToBase64(blobUrl: string): Promise<string | undefined> {
	return new Promise((resolve) => {
		fetch(blobUrl)
			.then((response) => response.blob())
			.then((blob) => {
				const reader = new FileReader();
				reader.readAsDataURL(blob);
				reader.onloadend = () => {
					const base64data = reader.result as string;
					resolve(base64data);
				};
			})
			.catch(() => {
				resolve(undefined);
			});
	});
}

function uint8ArrayToBase64Url(bytes: Uint8Array): string {
	const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToUint8Array(base64url: string): Uint8Array {
	const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
	const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
	const binary = atob(padded);
	return new Uint8Array(Array.from(binary, (char) => char.charCodeAt(0)));
}

// old browsers report the load through readyState
type LegacyScriptElement = HTMLScriptElement & {
	readyState?: string;
	onreadystatechange?: ((_: unknown, isAbort?: boolean) => void) | null;
};

function loadScript(path: string, cb?: () => void): void {
	const head = document.head || document.getElementsByTagName('head')[0];
	let s: LegacyScriptElement | null = document.createElement('script');

	s.src = path;
	head.appendChild(s);

	s.onload = s.onreadystatechange = function (_: unknown, isAbort?: boolean) {
		if (!s) return;
		if (isAbort || !s.readyState || s.readyState === 'loaded' || s.readyState === 'complete') {
			s = s.onload = s.onreadystatechange = null;
		}
		if (!isAbort && cb) {
			cb();
		}
	};
}

function getGreeting(): string {
	const hour = moment().hour();
	let greeting = 'Good ';
	if (hour > 4 && hour < 12) {
		greeting += 'Morning';
	} else if (hour >= 12 && hour < 18) {
		greeting += 'Afternoon';
	} else {
		greeting += 'Evening';
	}
	return greeting;
}

function copyToClipboard(text: string): void {
	// from http://stackoverflow.com/a/30810322
	const textarea = $('<textarea />');

	textarea.addClass('copyToClipboard');
	textarea.val(text);

	$('body').append(textarea);

	(textarea.get(0) as HTMLInputElement).select();

	try {
		document.execCommand('copy');
	} catch (_e) {
		// skip
	}

	textarea.remove();
}

function getIntervalText(startDate: string, endDate: string, addBrackets = true): string {
	let intervalDate = '';

	if (startDate) {
		if (addBrackets) {
			intervalDate += '(';
		}
		intervalDate += startDate;
		if (endDate && endDate !== startDate) {
			intervalDate += ' - ' + endDate;
		}
		if (addBrackets) {
			intervalDate += ')';
		}
	}

	return intervalDate;
}

interface ImageLoadStatus {
	image: HTMLImageElement;
	path: string;
	status: string;
}
function afterImagesLoaded(pathArray: string[]): Promise<PromiseSettledResult<ImageLoadStatus>[]> {
	return Promise.allSettled(
		pathArray.map((path: string) => {
			return new Promise<ImageLoadStatus>((resolve, reject) => {
				const img = new Image();

				img.onload = function () {
					resolve({ image: img, path, status: 'ok' });
				};

				img.onerror = function () {
					reject({ image: img, path, status: 'error' });
				};

				img.src = path;
			});
		}),
	);
}

function uniqueId(): number {
	const id = window.dboxUniqueId || 1;
	window.dboxUniqueId = id + 1;
	return id;
}

function redirect(url: string): void {
	document.location.href = url;
}

function reload(): void {
	document.location.reload();
}

function getInitials(name: string): string {
	return name
		? name
				.split(' ')
				.map((item) => item[0])
				.join('')
				.substr(0, 2)
		: '';
}

function replaceAll(string: string, str1: string, str2: string): string {
	return string.split(str1).join(str2);
}

function capitalize([first, ...rest]: string): string {
	return (first || '').toUpperCase() + rest.join('');
}

function ellipsizeText(text: string, limit: number): string {
	let ellipsizedText = '';

	if (!text) return ellipsizedText;

	if (text.length) {
		const wordArray = text.split(' ');
		let currentCharsNum = 0,
			nextWord: string | undefined = '';

		for (let i = 0; i < wordArray.length; i++) {
			ellipsizedText += wordArray[i];
			currentCharsNum = ellipsizedText.length;
			nextWord = wordArray[i + 1];

			if (nextWord && currentCharsNum + nextWord.length > limit) {
				ellipsizedText += '...';
				break;
			} else {
				ellipsizedText += ' ';
			}
		}
	}

	return ellipsizedText;
}

function generateNormalizedString(name: string, forceLowercase = false): string {
	// cleanup special characters: https://stackoverflow.com/a/37511463
	let normalizedName = name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

	if (forceLowercase) {
		normalizedName = normalizedName.toLowerCase();
	}

	// replace spaces with underscore
	normalizedName = normalizedName.replace(/\s+/g, '_');

	// remove everything except letters and numbers (no pipes allowed here bcos this is a part itself)
	normalizedName = normalizedName.replace(/[^a-zA-Z0-9_]/g, '');

	return normalizedName;
}

function snakeCaseToTitleCase(inputString: string) {
	// Split the string into words based on underscores
	const words = inputString.split('_');

	// Capitalize the first letter of each word and convert the rest to lowercase
	const titleCaseWords = words.map((word) => {
		// Check if the word is not empty
		if (word.length > 0) {
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		}
		return '';
	});

	// Join the title case words with spaces
	const titleCaseString = titleCaseWords.join(' ');

	return titleCaseString;
}

function stringifyAlphabetical(object: object = {}): string {
	/**
	 * Recursively sorts object keys alphabetically at all levels
	 * @param value - The value to process (object, array, or primitive)
	 * @returns The processed value with sorted keys
	 */
	function sortKeysRecursively(value: unknown): unknown {
		// Handle null/undefined
		if (value === null || value === undefined) {
			return value;
		}

		// Handle arrays - sort keys of each element
		if (Array.isArray(value)) {
			return value.map(sortKeysRecursively);
		}

		// Handle objects - sort keys and process nested values
		if (typeof value === 'object') {
			const keys = Object.keys(value).sort();
			const sortedObject: Record<string, unknown> = {};

			keys.forEach((key) => {
				sortedObject[key] = sortKeysRecursively((value as Record<string, unknown>)[key]);
			});

			return sortedObject;
		}

		return value;
	}

	const sortedObject = sortKeysRecursively(object);
	return JSON.stringify(sortedObject);
}

function getMostCommonElement<T>(arr: T[] = []): T | undefined {
	// https://stackoverflow.com/a/20762713
	return arr
		.sort((a, b) => arr.filter((v) => v === a).length - arr.filter((v) => v === b).length)
		.pop();
}

function escapeRegExp(string: string): string {
	// https://stackoverflow.com/a/6969486/7796544
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function formatObjectArrayAsTextTable(
	data: Record<string, AnySimpleValue>[],
	minSpacesBetweenColumns: number,
): string {
	if (!data.length) {
		return '';
	}

	const keys = Object.keys(data[0]!),
		longestWordsLengthByKey: Record<string, number> = {},
		appendMissingSpaces = (word: string, longestWordLength: number) => {
			const diff = longestWordLength - word.length;
			return word + (diff > 0 ? Array(diff + 1).join(' ') : '');
		};
	let formattedText = '';

	keys.forEach((key) => {
		longestWordsLengthByKey[key] = Math.max(
			...data.map((item) => String(item[key] || '-').length),
		);
	});

	data.forEach((item, index) => {
		keys.forEach((key, index) => {
			formattedText += appendMissingSpaces(
				String(item[key] || '-'),
				longestWordsLengthByKey[key] ?? 0,
			);
			formattedText +=
				index < keys.length - 1 ? Array(minSpacesBetweenColumns + 1).join(' ') : '';
		});

		formattedText += index < data.length - 1 ? '\n' : '';
	});

	return formattedText;
}

function getSourcesTexts(
	sources: { label: string }[],
	firstN = 2,
): { first: string; last: string; other: string } {
	const first = sources
		.slice(0, firstN)
		.map((source) => {
			return source.label;
		})
		.join(', ');

	const last = sources
		.slice(firstN)
		.map((source) => {
			return source.label;
		})
		.join(', ');

	let other = '';
	if (last) {
		const length = sources.slice(firstN).length;
		other = ` & ${length} other${length > 1 ? 's' : ''}`;
	}

	return { first, last, other };
}

function getPreviousOrNextItemByDirection<T>(
	items: T[],
	currentItem: T,
	direction: PaginationDirection,
): T {
	let index = items.indexOf(currentItem);
	if (direction === 'prev') {
		index--;

		if (index < 0) {
			index = items.length - 1;
		}
	} else {
		index++;

		if (index === items.length) {
			index = 0;
		}
	}

	return items[index]!;
}

function generateWordsArray(text: string): string[] {
	const regexExcludingWhiteSpaces = /[^\s]+/g;

	const texts = [...((text || '').match(regexExcludingWhiteSpaces) || [])];
	return uniq(texts);
}

function createCommaDelimitedStringWithAnd(words: string[], skipAnd?: boolean) {
	let finalText = '';
	for (let i = 0; i < words.length; i++) {
		if (i >= 1) {
			if (i === words.length - 1 && !skipAnd) {
				finalText += ' and ';
			} else {
				finalText += ', ';
			}
		}
		finalText += words[i];
	}

	return finalText;
}

function createDeliveryTypesArray(deliveryTypeNumbers: DeliveryType[], isCapitalized = false) {
	const deliveryTypes = [];
	if (deliveryTypeNumbers?.includes(DeliveryType.PUSH)) {
		const typeName = 'mobile';
		deliveryTypes.push(isCapitalized ? uppercaseFirstLetter(typeName) : typeName);
	}

	if (deliveryTypeNumbers?.includes(DeliveryType.EMAIL)) {
		const typeName = 'email';
		deliveryTypes.push(isCapitalized ? uppercaseFirstLetter(typeName) : typeName);
	}

	if (deliveryTypeNumbers?.includes(DeliveryType.SLACK)) {
		const typeName = 'slack';
		deliveryTypes.push(isCapitalized ? uppercaseFirstLetter(typeName) : typeName);
	}

	return deliveryTypes;
}

function prettyJsonStringify(data: unknown): string {
	return JSON.stringify(data, null, '\t');
}

function safeJSONStringify(obj: unknown): string {
	// avoiding "Converting circular structure to JSON" errors
	const seen = new WeakSet();
	return JSON.stringify(obj, (_k, v) => {
		if (v !== null && typeof v === 'object') {
			if (seen.has(v)) {
				return;
			}
			seen.add(v);
		}
		return v;
	});
}

// @date - '2020-02-26 15:00:00'
function getTs(date: string): number {
	return new Date(`${date} UTC`).getTime() / 1000;
}

function repeatArrayItems<T>(arr: T[], targetLength: number) {
	if (arr.length >= targetLength) {
		return arr.slice(0, targetLength); // If the array is already at or exceeds the target length, return a slice of it.
	}

	const repeatedItems = [...arr];
	for (let i = 0; i < targetLength - arr.length; i++) {
		repeatedItems.push(arr[i % arr.length]!);
	}

	return repeatedItems;
}

function toSorted<T>(arr: T[], sortFn: (a: T, b: T) => number) {
	const newArr = [...arr]; // Array.from(arr), arr.slice()
	return newArr.sort(sortFn);
}

function sortArrayBasedOnSubset<T>(arr: T[], subset: T[]) {
	/*
		Modifies the original arr array

		- If both elements are in the subset, order them based on their indices in the subset.
		- If only one element is in the subset, move that element towards the end of the arr array.
		- If neither element is in the subset, leave their order unchanged.
	*/
	arr.sort((a, b) => {
		const aIndex = subset.indexOf(a);
		const bIndex = subset.indexOf(b);

		if (aIndex === -1 && bIndex === -1) {
			return 0; // both elements are not in subset
		} else if (aIndex !== -1 && bIndex !== -1) {
			return aIndex - bIndex; // both elements are in subset
		} else if (aIndex !== -1) {
			return 1; // only a is in subset
		} else {
			return -1; // only b is in subset
		}
	});

	return arr;
}

function arrayMove<T>(arr: (T | undefined)[], oldIndex: number, newIndex: number) {
	if (newIndex >= arr.length) {
		let missingItemsLength = newIndex - arr.length + 1;

		while (missingItemsLength--) {
			arr.push(undefined);
		}
	}

	arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]); // Modifies passed arr

	return arr as T[];
}

function switchDotsAndCommas(inputString: string) {
	return inputString
		.split('')
		.map((char) => (char === ',' ? '.' : char === '.' ? ',' : char))
		.join('');
}

function generateHash(length = 32) {
	const getRandomSubstring = () => Math.random().toString(36).slice(2);
	return (getRandomSubstring() + getRandomSubstring()).slice(0, length);
}

function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]) {
	// pick(obj, 'foo-bar', 'bar', 'baz')

	return Object.fromEntries(
		keys.filter((key) => key in obj).map((key) => [key, obj[key]]),
	) as Pick<T, K>;

	/*
		For obj = { 'foo-bar': 1, bar: 2, qux: 3 }
		If keys are foo-bar, bar, baz
			- pick: { 'foo-bar': 1, bar: 2 }
	*/
}

function omit<T extends object, K extends keyof T>(obj: T, ...keys: K[]) {
	// omit(obj, 'foo', 'bar') - shallow copy without the given keys

	return Object.fromEntries(
		Object.entries(obj).filter(([key]) => !(keys as PropertyKey[]).includes(key)),
	) as Omit<T, K>;
}

function deleteKeys<T extends object>(obj: T, ...keys: (keyof T)[]): void {
	// deleteKeys(obj, 'foo', 'bar') - `delete` in place, also for keys the type declares as required

	keys.forEach((key) => {
		delete obj[key];
	});
}

function sortByPopularity<T extends { usedCount: number }>(item: T[]): T[] {
	return toSorted(item, (a, b) => b.usedCount - a.usedCount);
}

function copyObject<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

function shuffleArray<T>(array: T[]) {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		const el1 = array[i]!;
		const el2 = array[j]!;
		array[i] = el2;
		array[j] = el1;
	}
	return array;
}

function hasOccurredWithin24Hours(timestamp: number): boolean {
	const twentyFourHoursAgo = moment().subtract(24, 'hours').unix();
	return timestamp >= twentyFourHoursAgo;
}

function getReturnUrlFromTransition(transition: PublicTransition, router: RouterService): string {
	const fromName = transition.from?.name;

	let exitUrl = '';

	if (fromName) {
		const models: string[] = [];

		let tParent = transition.from;
		while (tParent) {
			if (tParent.params) {
				const params = Object.values(tParent.params) as string[];
				if (params[0]) {
					models.push(params[0]);
				} else if (tParent.attributes?.id) {
					models.push(tParent.attributes.id);
				}
			}
			tParent = tParent.parent as RouteInfoWithAttributes;
		}

		models.reverse();
		exitUrl = router.urlFor(fromName, ...models);
	}

	return exitUrl;
}

function isLessThan90Days(timestamp: number): boolean {
	return moment().diff(moment.unix(timestamp), 'days') < 90;
}

async function getResourceSizeFromUrl(resourceUrl: string): Promise<number> {
	return fetch(resourceUrl, { method: 'HEAD' })
		.then((response) => {
			if (response.ok) {
				const contentLength = response.headers.get('content-length');
				if (contentLength) {
					return +contentLength || 0;
				}
			}
			return 0;
		})
		.catch(() => {
			return 0;
		});
}

function getPositionFromOrder(order: number): { row: number; column: number } {
	const sOrder = String(order);
	return {
		row: +sOrder.substring(0, sOrder.length - 1),
		column: +sOrder[sOrder.length - 1]!,
	};
}

function getOrderFromPosition(row: number, column: number): number {
	return row * 10 + column;
}

function getSafeBackgroundColorStyle(colorName: string, colorName2?: string) {
	const formattedColor = colorName2
		? `linear-gradient(to right, var(--${colorName}-80) 0%, var(--${colorName2}-80) 100%)`
		: `var(--${colorName}-80)`;

	return htmlSafe(`background: ${formattedColor}`);
}

function getShadowDomMode(email: string) {
	return isPlaywrightEmail(email) ? 'open' : 'closed';
}

function normalizeFilters(
	filters?: DSSetup.MetricSetupFiltersData | null,
): DSSetup.MetricSetupFiltersData | null | undefined {
	if (filters && Object.keys(filters || {}).length) {
		const filtersCopy = copyObject(filters);
		filtersCopy.groupItems.forEach((groupItem) => {
			groupItem.filterItems.forEach((item) => {
				// unit: '-0' on FE === '' on BE (only empty)
				if (item.type === FilterItemType.UNIT) {
					if (item.values[0] === Unit.EMPTY_BE) {
						item.values = [Unit.EMPTY];
					}
				}
			});
		});
		return filtersCopy;
	}
	return null;
}

function serializeFilters(
	filters?: DSSetup.MetricSetupFiltersData | null,
): DSSetup.MetricSetupFiltersData | null | undefined {
	if (filters && Object.keys(filters || {}).length) {
		const filtersCopy = copyObject(filters);
		filtersCopy.groupItems.forEach((groupItem) => {
			groupItem.filterItems.forEach((item) => {
				// unit: '-0' on FE === '' on BE (only empty)
				if (item.type === FilterItemType.UNIT) {
					if (item.values[0] === Unit.EMPTY) {
						item.values = [Unit.EMPTY_BE];
					}
				}
			});
		});
		return filtersCopy;
	}
	return null;
}

function normalizeMetricSetupData(
	metricSetupData?: DSSetup.MetricSetupData | null,
): DSSetup.MetricSetupData | null {
	if (!metricSetupData) {
		return null;
	}
	const normalizedMetricSetupData = copyObject(metricSetupData);
	if (normalizedMetricSetupData?.filters) {
		normalizedMetricSetupData.filters =
			normalizeFilters(normalizedMetricSetupData.filters) || undefined;
	}
	return normalizedMetricSetupData;
}

function serializeMetricSetupData(
	metricSetupData?: DSSetup.MetricSetupData | null,
): DSSetup.MetricSetupData | null {
	if (!metricSetupData) {
		return null;
	}
	const serializedMetricSetupData = copyObject(metricSetupData);
	if (serializedMetricSetupData?.filters) {
		serializedMetricSetupData.filters =
			serializeFilters(serializedMetricSetupData.filters) || undefined;
	}
	return serializedMetricSetupData;
}

function compact<T>(array: (T | null | undefined)[]): T[] {
	return array.filter((item) => item !== undefined && item !== null);
}

function uniq<T>(array: T[]): T[] {
	return [...new Set(array)];
}

function uniqBy<T>(array: T[], key: keyof T): T[] {
	return array.reduce((unique, item) => {
		if (!unique.find((i) => i[key] === item[key])) {
			unique.push(item);
		}
		return unique;
	}, [] as T[]);
}

function sortBy<T>(array: T[], key1: keyof T, key2?: keyof T): T[] {
	return Array.from(array).sort((a: T, b: T) => {
		// Helper function to compare any two values
		const compare = (val1: T[keyof T], val2: T[keyof T]): number => {
			// Handle null/undefined values
			const val1IsNull = val1 === null || val1 === undefined,
				val2IsNull = val2 === null || val2 === undefined;
			if (val1IsNull && val2IsNull) return 0;
			if (val1IsNull) return 1; // null values go last
			if (val2IsNull) return -1;

			// Handle different types
			if (typeof val1 === 'string' && typeof val2 === 'string') {
				return val1.localeCompare(val2);
			} else if (typeof val1 === 'boolean' && typeof val2 === 'boolean') {
				return val1 === val2 ? 0 : val1 ? -1 : 1;
			} else {
				// For numbers and other comparable types
				return val1 < val2 ? -1 : val1 > val2 ? 1 : 0;
			}
		};

		// Compare by first key
		const result = compare(a[key1], b[key1]);

		// If values are equal and second key is provided, compare by second key
		if (result === 0 && key2) {
			return compare(a[key2], b[key2]);
		}

		return result;
	});
}

function glueDimensionValues(
	dimensionValues: DSData.DimensionsAndValuesMap | null | undefined,
): string {
	return uniq(Object.values(dimensionValues || {})).join(' / ');
}

function buildDataLabelPartsFromDimensionsAndValuesMap(
	dimensionValues: DSData.DimensionsAndValuesMap | null | undefined,
	dimensions: DSSetup.DimensionItem[] = [],
): DataLabelPart[] {
	return Object.entries(dimensionValues || {}).map(([key, value]) => ({
		type: DataLabelType.DIMENSION,
		value,
		dimension: dimensions.find((d) => d.key === key) || { key, value: '' },
	}));
}

/**
 * Watch-hack escape hatch. In local `watch` (dev) builds, tracked-property updates that happen
 * in the same runloop they were consumed in throw a "you attempted to update X, but it had already
 * been used previously" backtracking assertion. Production strips these assertions, so the code is
 * fine there — the failure is dev-only noise. Wrapping such an update in this helper defers it to
 * the next runloop when watch-hack mode is active, silencing the assertion locally.
 *
 * Guardrails so this never affects a real build:
 * - It only defers when `window.env === AppEnv.LOCAL`. `window.env` is set server-side, so a
 *   deployed (develop/production) server can never report `local` — the helper is a plain
 *   passthrough there regardless of the flag.
 * - It only defers when `window.isWatchHackMode` is on, which is opt-in via `preferWatchHackMode`
 *   in `app-loaded-events` (committed `false`, blocked from being committed `true` by lefthook).
 * - Every deferral logs a warning so an opted-in dev still sees exactly which sites are papered
 *   over, and can assess whether to verify the change against a production build before shipping.
 *
 * @param label identifies the call site in the console warning
 */
function checkToApplyWatchHack(label: string, fn: () => void) {
	if (window.env === AppEnv.LOCAL && window.isWatchHackMode) {
		// eslint-disable-next-line no-console
		console.warn(`[watch-hack] deferring to next() — not prod-safe as-is: ${label}`);
		next(fn);
	} else {
		fn();
	}
}

export {
	equal,
	isAlive,
	utf8_to_b64,
	b64_to_utf8,
	blobUrlToBase64,
	loadScript,
	getGreeting,
	copyToClipboard,
	getIntervalText,
	afterImagesLoaded,
	uniqueId,
	redirect,
	reload,
	getInitials,
	replaceAll,
	capitalize,
	ellipsizeText,
	generateNormalizedString,
	snakeCaseToTitleCase,
	stringifyAlphabetical,
	getMostCommonElement,
	escapeRegExp,
	formatObjectArrayAsTextTable,
	getSourcesTexts,
	getPreviousOrNextItemByDirection,
	generateWordsArray,
	createCommaDelimitedStringWithAnd,
	createDeliveryTypesArray,
	prettyJsonStringify,
	safeJSONStringify,
	getTs,
	repeatArrayItems,
	toSorted,
	arrayMove,
	sortArrayBasedOnSubset,
	switchDotsAndCommas,
	generateHash,
	pick,
	omit,
	deleteKeys,
	sortByPopularity,
	copyObject,
	shuffleArray,
	hasOccurredWithin24Hours,
	getReturnUrlFromTransition,
	isLessThan90Days,
	getResourceSizeFromUrl,
	getSafeBackgroundColorStyle,
	getShadowDomMode,
	getPositionFromOrder,
	getOrderFromPosition,
	normalizeFilters,
	serializeFilters,
	normalizeMetricSetupData,
	serializeMetricSetupData,
	compact,
	uniq,
	uniqBy,
	sortBy,
	checkToApplyWatchHack,
	uint8ArrayToBase64Url,
	base64UrlToUint8Array,
	glueDimensionValues,
	buildDataLabelPartsFromDimensionsAndValuesMap,
};
